import { Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { MatDialogConfig } from '@angular/material/dialog';

/** Окно закрывается, если стянуто больше чем на эту долю своей высоты… */
const DISMISS_RATIO = 0.25;
/** …или отпущено быстрее, px/мс */
const DISMISS_VELOCITY = 0.5;
/** Сдвиг пальца, после которого понятно направление жеста, px */
const SLOP = 4;
/** Скорость считается по последним точкам за это время, мс */
const VELOCITY_WINDOW = 100;
const DURATION = 300;
/** Кривая системного окна iOS: почти весь путь в начале — продолжает движение пальца */
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';
/** Закрытие кнопкой (добавить, удалить, крестик): плавный разгон и торможение,
 *  чтобы было видно, что окно уезжает, а не исчезает */
const CLOSE_DURATION = 380;
const CLOSE_EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** Открытые окна: прокрутка страницы закрыта, пока открыто хотя бы одно */
let openSheets = 0;

/**
 * Конфиг MatDialog для нижнего окна. Прокрутку страницы закрывает директива:
 * блокировка CDK ставит <html> position: fixed, и мобильный браузер
 * разворачивал адресную строку. Анимацию открытия и закрытия ведёт директива
 */
export function sheetDialogConfig<D>(data?: D): MatDialogConfig<D> {
  return {
    panelClass: 'custom-dialog-container',
    backdropClass: ['cdk-overlay-dark-backdrop', 'sheet-backdrop'],
    position: { bottom: '0' },
    width: '100%',
    maxWidth: '100%',
    scrollStrategy: new NoopScrollStrategy(),
    enterAnimationDuration: 0,
    exitAnimationDuration: 0,
    data,
  };
}

type Mode = 'idle' | 'pending' | 'drag' | 'native';

/**
 * Нижнее окно (bottom sheet): выезжает снизу, закрывается свайпом вниз.
 *
 * Окно тянется, только если палец пошёл вниз, а содержимое ([appSheet])
 * в начале — с самого касания; иначе это прокрутка или нажатие. Если
 * содержимое доехало до начала посреди жеста, окно в этом жесте не
 * двигается. Закрывается, если стянуто на четверть или отпущено быстро;
 * иначе возвращается. Двигается через transform, затемнение светлеет
 * вместе с ним
 */
@Directive({
  selector: '[appSheet]',
  exportAs: 'appSheet',
  standalone: false
})
export class SheetDirective implements OnInit, OnDestroy {
  constructor(private elementRef: ElementRef<HTMLElement>, private zone: NgZone) {}

  /** Прокручиваемое содержимое окна */
  @Input('appSheet') scroll: HTMLElement | null = null;

  /** Окно уехало вниз — пора закрывать диалог */
  @Output() sheetClosed = new EventEmitter<void>();

  private mode: Mode = 'idle';
  private startX = 0;
  private startY = 0;
  private offset = 0;
  private samples: { t: number; y: number }[] = [];
  private closing = false;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  private get el(): HTMLElement {
    return this.elementRef.nativeElement;
  }

  /** Затемнение этого окна: в обёртке оверлея (popover) или перед ней */
  private get backdrop(): HTMLElement | null {
    const wrapper = this.el.closest('.cdk-global-overlay-wrapper');
    const previous = wrapper?.previousElementSibling;
    return wrapper?.querySelector<HTMLElement>(':scope > .sheet-backdrop')
      ?? (previous?.classList.contains('sheet-backdrop') ? previous as HTMLElement : null);
  }

  ngOnInit(): void {
    if (openSheets++ === 0) document.documentElement.classList.add('sheet-open');

    this.zone.runOutsideAngular(() => {
      this.el.addEventListener('touchstart', this.onStart, { passive: true });
      this.el.addEventListener('touchmove', this.onMove, { passive: false });
      this.el.addEventListener('touchend', this.onEnd);
      this.el.addEventListener('touchcancel', this.onCancel);

      // Выезд снизу. Затемнение CDK добавляет после содержимого — к кадру оно есть
      this.el.style.transform = 'translateY(100%)';
      requestAnimationFrame(() => {
        void this.el.offsetHeight;
        this.move(0, DURATION);
      });
    });
  }

  /**
   * Увести окно вниз и сообщить sheetClosed. Кнопкой — плавно; после свайпа
   * (gesture) — быстрой кривой, вслед за пальцем
   */
  dismiss(gesture = false): void {
    if (this.closing) return;
    this.closing = true;
    this.mode = 'idle';
    const duration = gesture ? DURATION : CLOSE_DURATION;
    this.move(this.el.offsetHeight, duration, gesture ? EASE : CLOSE_EASE);
    setTimeout(() => this.zone.run(() => this.sheetClosed.emit()), this.reducedMotion ? 0 : duration);
  }

  private onStart = (event: TouchEvent): void => {
    if (this.closing || event.touches.length > 1) {
      this.mode = 'native';
      return;
    }
    const touch = event.touches[0];
    const target = event.target as HTMLElement;
    const inScroll = !!this.scroll?.contains(target);
    const atTop = !inScroll || (this.scroll?.scrollTop ?? 0) <= 0;
    this.mode = atTop && !target.closest('input, textarea, select, [contenteditable]') ? 'pending' : 'native';
    this.startX = touch.clientX;
    this.startY = touch.clientY;
    this.offset = 0;
    this.samples = [{ t: event.timeStamp, y: touch.clientY }];
  };

  private onMove = (event: TouchEvent): void => {
    if (this.mode === 'native' || this.mode === 'idle') return;
    const touch = event.touches[0];
    const dx = touch.clientX - this.startX;
    const dy = touch.clientY - this.startY;

    if (this.mode === 'pending') {
      if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
      // Вниз и скорее вертикально — окно; иначе прокрутка, до конца жеста.
      // Браузер уже прокручивает (событие не отменить) — тоже прокрутка
      if (dy > 0 && dy >= Math.abs(dx) && event.cancelable) {
        this.mode = 'drag';
      } else {
        this.mode = 'native';
        return;
      }
    }

    event.preventDefault();
    this.offset = Math.max(0, dy);
    this.samples.push({ t: event.timeStamp, y: touch.clientY });
    while (this.samples.length > 2 && event.timeStamp - this.samples[0].t > VELOCITY_WINDOW) {
      this.samples.shift();
    }
    this.move(this.offset);
  };

  private onEnd = (): void => {
    if (this.mode !== 'drag') {
      this.mode = 'idle';
      return;
    }
    this.mode = 'idle';
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    const velocity = last.t > first.t ? (last.y - first.y) / (last.t - first.t) : 0;
    if (this.offset > this.el.offsetHeight * DISMISS_RATIO || (this.offset > 0 && velocity > DISMISS_VELOCITY)) {
      this.dismiss(true);
    } else {
      this.move(0, DURATION);
    }
  };

  private onCancel = (): void => {
    if (this.mode === 'drag') this.move(0, DURATION);
    this.mode = 'idle';
  };

  /** Сдвиг окна вниз на offset px за duration мс (0 — сразу); затемнение — пропорционально */
  private move(offset: number, duration = 0, ease = EASE): void {
    const transition = duration && !this.reducedMotion ? `${duration}ms ${ease}` : '';
    this.el.style.transition = transition ? `transform ${transition}` : 'none';
    this.el.style.transform = offset ? `translateY(${offset}px)` : '';

    const backdrop = this.backdrop;
    if (backdrop) {
      backdrop.style.transition = transition ? `opacity ${transition}` : 'none';
      backdrop.style.opacity = offset ? String(Math.max(0, 1 - offset / this.el.offsetHeight)) : '';
    }
  }

  ngOnDestroy(): void {
    if (--openSheets === 0) document.documentElement.classList.remove('sheet-open');
    this.el.removeEventListener('touchstart', this.onStart);
    this.el.removeEventListener('touchmove', this.onMove);
    this.el.removeEventListener('touchend', this.onEnd);
    this.el.removeEventListener('touchcancel', this.onCancel);
  }
}
